import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { useApi } from '@/context/api';
import { useAuth } from '@/context/auth';

import type {
  CreateEstimationItemAssumptionRequest,
  UpdateEstimationItemAssumptionRequest,
} from '../../schema/data-contracts';
import type { MutationProps } from '../types';

export const estimationItemAssumptionKey = 'estimation-item-assumption';
export const estimationItemAssumptionsKey = 'estimation-item-assumptions';

export const useEstimationItemAssumptionQueryActions = () => {
  const { auth } = useAuth();
  const {
    estimationItemAssumptionController: {
      v1EstimationItemsAssumptionsDetail,
      v1EstimationItemsAssumptionsList,
      v1EstimationItemsAssumptionsUpdate,
      v1EstimationItemsAssumptionsDelete,
      v1EstimationItemsAssumptionsCreate,
    },
    estimationController: { v1EstimationsEstimationItemsAssumptionsList },
  } = useApi();

  const queryClient = useQueryClient();

  const useGetEstimationItemAssumption = (estimationItemAssumptionId: string | undefined) =>
    useQuery({
      enabled: Boolean(auth) && !!estimationItemAssumptionId,
      queryKey: [
        estimationItemAssumptionKey,
        'estimationItemAssumptionId',
        estimationItemAssumptionId,
      ],
      queryFn: async () => {
        const { data } = await v1EstimationItemsAssumptionsDetail(estimationItemAssumptionId!);
        return data;
      },
    });

  const useGetEstimationItemAssumptionsByEstimationId = (estimationId: string | undefined) =>
    useQuery({
      enabled: Boolean(auth) && !!estimationId,
      queryKey: [estimationItemAssumptionsKey, { estimationId }],
      queryFn: async () => {
        const { data } = await v1EstimationsEstimationItemsAssumptionsList(estimationId!);
        return data;
      },
    });

  const useGetEstimationItemAssumptionsByEstimationItemId = (
    estimationItemId: string | undefined,
  ) =>
    useQuery({
      enabled: Boolean(auth) && !!estimationItemId,
      queryKey: [estimationItemAssumptionsKey, { estimationItemId }],
      queryFn: async () => {
        const { data } = await v1EstimationItemsAssumptionsList(estimationItemId!);
        return data;
      },
    });

  const useCreateEstimationItemAssumption = (props?: MutationProps) =>
    useMutation({
      ...props,
      mutationKey: ['estimation-item-assumption-create'],
      mutationFn: async ({
        estimationItemId,
        estimationAssumption,
      }: {
        estimationItemId: string;
        estimationAssumption: CreateEstimationItemAssumptionRequest;
      }) => {
        const { data } = await v1EstimationItemsAssumptionsCreate(
          estimationItemId,
          estimationAssumption,
        );
        return data;
      },
      onSuccess: async () => {
        await queryClient.invalidateQueries({
          queryKey: [estimationItemAssumptionsKey],
        });
      },
    });

  const useUpdateEstimationItemAssumption = (props?: MutationProps) =>
    useMutation({
      ...props,
      mutationKey: ['estimation-item-assumption-update'],
      mutationFn: async ({
        estimationItemAssumptionId,
        estimationItemAssumption,
      }: {
        estimationItemAssumptionId: string;
        estimationItemAssumption: UpdateEstimationItemAssumptionRequest;
      }) => {
        const { data } = await v1EstimationItemsAssumptionsUpdate(
          estimationItemAssumptionId,
          estimationItemAssumption,
        );
        return data;
      },
      onSuccess: async (data) => {
        await queryClient.invalidateQueries({
          queryKey: [estimationItemAssumptionsKey],
        });
        await queryClient.invalidateQueries({
          queryKey: [estimationItemAssumptionKey, 'estimationItemAssumptionId', data.id],
          exact: true,
        });
      },
    });

  const useDeleteEstimationItemsAssumption = (props?: MutationProps) =>
    useMutation({
      ...props,
      mutationKey: ['estimation-items-assumption-delete'],
      mutationFn: async (assumptionId: string) => {
        const { data } = await v1EstimationItemsAssumptionsDelete(assumptionId);
        return data;
      },
      onSuccess: async () => {
        await queryClient.invalidateQueries({
          queryKey: [estimationItemAssumptionsKey],
        });
      },
    });

  return {
    useGetEstimationItemAssumption,
    useGetEstimationItemAssumptionsByEstimationId,
    useGetEstimationItemAssumptionsByEstimationItemId,
    useCreateEstimationItemAssumption,
    useUpdateEstimationItemAssumption,
    useDeleteEstimationItemsAssumption,
  };
};
